import React, { ReactNode } from 'react'

import { NarrativeHeroProps } from 'lib/components/NarrativeHero/NarrativeHero'

export interface ClaimsPageContent {
  hero: NarrativeHeroProps
  claimsProcess: {
    title: string
    steps: {
      title: string
      description: string
    }[]
  }
  claimsForms: {
    title: string
    forms: {
      name: string
      href: string
    }[]
  }
  contactInfo: {
    title: string
    sections: {
      title: string
      content: (ReactNode | string)[]
    }[]
  }
}

export interface ShipplayClaimsContent {
  hero: {
    imgUrl: string
    title: {
      prefix: string
      emphasis: string
      suffix?: string
    }
  }
  claimsProcess: {
    title: string
    steps: { title: string; description: ReactNode }[]
  }
  claimsForms: {
    title: string
    forms: { name: string; href: string; description: ReactNode }[]
  }
  contactInfo: {
    title: string
    sections: { title: string; lines: ReactNode[] }[]
  }
}

const hero: NarrativeHeroProps = {
  header: 'Ship&Play Claims Process',
  subHeader:
    'Learn about our claims process and file a claim for damaged, lost, or delayed shipments.',
}

const claimsProcess = {
  title: 'Claims Process',
  steps: [
    {
      title: 'File Claim',
      description:
        'To begin the claims process you will select the appropriate online form below and ' +
        'fill out all required fields. All claims must be filed within 10 days of the ' +
        'actual or scheduled delivery date.',
    },
    {
      title: 'Claim Reviewed',
      description:
        'A claims specialist will review and file your claim within 1-2 business days. ' +
        'If additional information is needed, a claims specialist will contact you by ' +
        'phone or email.',
    },
    {
      title: 'Claim Filed',
      description:
        'Once your claim has been filed you can expect to receive an update on your claim ' +
        'within 7-10 business days. At this time we will notify you if your claim has ' +
        'been approved.',
    },
    {
      title: 'Claim Approved',
      description:
        'Once your claim has been approved we will mail your reimbursement check via USPS. ' +
        'The check will be sent to the address provided in your claims submission.',
    },
  ],
}

const claimsForms = {
  title: 'Ship&Play Claims',
  forms: [
    {
      name: 'Damaged/Missing Items Claim Form',
      href: 'https://form.jotform.com/232243546676158',
    },
    {
      name: 'Shipment Protection Claim Form',
      href: 'https://gse.jotform.com/232243989448166',
    },
  ],
}

const contactInfo = {
  title: 'Contact Claims at Ship&Play',
  sections: [
    {
      title: 'Claims Department',
      content: [
        'Email: info@shipplay.com',
        'Phone: (561) 429-3900',
        'Hours: Monday - Friday, 9:00 AM - 6:00 PM EST',
      ],
    },
    {
      title: 'Mailing Address',
      content: [
        'Ship&Play Claims Department',
        '1140 N. Jog Road Suite 300',
        'Palm Beach Gardens, FL 33418',
      ],
    },
  ],
}

const content: ClaimsPageContent = {
  hero,
  claimsProcess,
  claimsForms,
  contactInfo,
}

export const shipplayContent: ShipplayClaimsContent = {
  hero: {
    imgUrl: '/images/shipplay/hero/paperwork.webp',
    title: {
      prefix: 'Ship',
      emphasis: '&Play',
      suffix: ' Claims Process.',
    },
  },
  claimsProcess: {
    title: 'Ship&Play Claims Process',
    steps: [
      {
        title: 'File Claim',
        description: (
          <>
            To begin the claims process you will select the appropriate online form below and fill
            out all required fields.{' '}
            <strong>
              All claims must be filed within 10 days of the actual or scheduled delivery date.
            </strong>
          </>
        ),
      },
      claimsProcess.steps[1],
      claimsProcess.steps[2],
      claimsProcess.steps[3],
    ],
  },
  claimsForms: {
    title: 'File a Claim',
    forms: [
      {
        name: 'Damaged/Missing Items Claim Form',
        href: 'https://form.jotform.com/232243546676158',
        description:
          'Use this form if your equipment arrived damaged or items were missing from your ' +
          'shipment. File within 10 days of delivery and include photos and a description of ' +
          'the damage or missing items.',
      },
      {
        name: 'Shipment Protection Claim Form',
        href: 'https://gse.jotform.com/232243989448166',
        description: (
          <>
            Use this form if your shipment was lost, delayed, or otherwise didn&rsquo;t arrive as
            scheduled and you purchased shipment protection at checkout. File within 10 days of the
            scheduled delivery date to request reimbursement under your protection coverage.
          </>
        ),
      },
    ],
  },
  contactInfo: {
    title: 'Contact Our Claims Department',
    sections: [
      {
        title: 'Contact Info',
        lines: [
          <>
            Email:{' '}
            <a href="mailto:info@shipplay.com" className="snplay-link">
              info@shipplay.com
            </a>
          </>,
          <>
            Phone:{' '}
            <a href="tel:+15614293900" className="snplay-link">
              (561) 429-3900
            </a>
          </>,
          <>Hours: Monday &mdash; Friday, 9:00 AM &mdash; 6:00 PM EST</>,
        ],
      },
      {
        title: 'Mailing Address',
        lines: [
          <span key="name" className="snplay:font-semibold">
            Ship&amp;Play Claims Department
          </span>,
          <React.Fragment key="addr">
            1140 N. Jog Road Suite 300 <br />
            Palm Beach Gardens, FL 33418
          </React.Fragment>,
        ],
      },
    ],
  },
}

export default content
